/**
 * OpenAI Adapter
 * Wraps the OpenAI SDK to provide a clean abstraction layer
 *
 * Benefits:
 * - Centralized API key management
 * - Easy to swap AI providers (Claude, Gemini, etc.)
 * - Consistent error handling
 * - Feature availability checks
 */
import { gateway, generateObject, generateText } from "ai";
import type { LanguageModel } from "ai";
import { z } from "zod";

import { AI_MODELS, AI_RETRY_CONFIG, AI_TEMPERATURES } from "@/lib/config/ai-config";
import { features, serverEnv } from "@/lib/config/env";

/**
 * Check if OpenAI is available
 */
export function isOpenAIAvailable(): boolean {
  return features.hasOpenAI;
}

/**
 * Get the OpenAI model instance
 * Returns null if API key is not configured
 */
export function getOpenAIModel(modelName?: string): LanguageModel | null {
  if (!isOpenAIAvailable()) {
    return null;
  }
  return gateway(modelName || AI_MODELS.DEFAULT);
}

/**
 * Generate text using OpenAI
 * Returns null if OpenAI is not available
 */
export async function generateAIText(options: {
  prompt: string;
  model?: string;
  temperature?: number;
  maxOutputTokens?: number;
  system?: string;
}): Promise<{ text: string; usage?: { promptTokens: number; completionTokens: number } } | null> {
  const model = getOpenAIModel(options.model);
  if (!model) {
    return null;
  }

  try {
    const result = await generateText({
      model,
      prompt: options.prompt,
      temperature: options.temperature ?? AI_TEMPERATURES.BALANCED,
      maxOutputTokens: options.maxOutputTokens,
      system: options.system,
    });

    return {
      text: result.text,
      usage: result.usage
        ? {
            promptTokens: result.usage.inputTokens ?? 0,
            completionTokens: result.usage.outputTokens ?? 0,
          }
        : undefined,
    };
  } catch (error) {
    console.error("[OpenAI Adapter] Text generation failed:", error);
    throw error;
  }
}

/**
 * Generate structured object using OpenAI
 * Returns null if OpenAI is not available
 */
export async function generateAIObject<T>(options: {
  prompt: string;
  schema: z.ZodSchema<T>;
  model?: string;
  temperature?: number;
  system?: string;
}): Promise<{ object: T; usage?: { promptTokens: number; completionTokens: number } } | null> {
  const model = getOpenAIModel(options.model);
  if (!model) {
    return null;
  }

  try {
    const result = await generateObject({
      model,
      prompt: options.prompt,
      schema: options.schema,
      temperature: options.temperature ?? AI_TEMPERATURES.EXTRACTION,
      system: options.system,
    });

    return {
      object: result.object,
      usage: result.usage
        ? {
            promptTokens: result.usage.inputTokens ?? 0,
            completionTokens: result.usage.outputTokens ?? 0,
          }
        : undefined,
    };
  } catch (error) {
    console.error("[OpenAI Adapter] Object generation failed:", error);
    throw error;
  }
}

/**
 * Execute with retry logic
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options?: {
    maxRetries?: number;
    initialDelay?: number;
    maxDelay?: number;
    backoffMultiplier?: number;
  }
): Promise<T> {
  const maxRetries = options?.maxRetries ?? AI_RETRY_CONFIG.MAX_RETRIES;
  const initialDelay = options?.initialDelay ?? AI_RETRY_CONFIG.INITIAL_DELAY;
  const maxDelay = options?.maxDelay ?? AI_RETRY_CONFIG.MAX_DELAY;
  const backoffMultiplier = options?.backoffMultiplier ?? AI_RETRY_CONFIG.BACKOFF_MULTIPLIER;

  let lastError: Error | null = null;
  let delay = initialDelay;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      if (attempt < maxRetries) {
        console.warn(`[OpenAI Adapter] Attempt ${attempt + 1} failed, retrying in ${delay}ms...`);
        await new Promise((resolve) => setTimeout(resolve, delay));
        delay = Math.min(delay * backoffMultiplier, maxDelay);
      }
    }
  }

  throw lastError;
}

/**
 * Re-export AI SDK utilities for convenience
 */
export { generateObject, generateText, gateway } from "ai";
